import { ConnectionLineComponentProps, Edge, Position, getSmoothStepPath, useStore } from '@xyflow/react';
import { FC, RefObject } from 'react';

type DraggedLine = {
  id: string;
  x: number;
  y: number;
  position: Position;
};

// Draws one line to the cursor per connection in the bundle instead of react-flow's single line.
// A factory over the ref keeps the component identity stable while still reading the current bundle.
const createReconnectConnectionLine = (bundleRef: RefObject<Edge[]>): FC<ConnectionLineComponentProps> => {
  const ReconnectConnectionLine: FC<ConnectionLineComponentProps> = ({
    fromX,
    fromY,
    fromPosition,
    toX,
    toY,
    toPosition
  }) => {
    const nodeLookup = useStore((state) => state.nodeLookup);

    const anchors: DraggedLine[] = (bundleRef.current ?? []).flatMap((edge) => {
      const node = nodeLookup.get(edge.source);
      const handles = node?.internals.handleBounds?.source;
      const handle = handles?.find((candidate) => candidate.id === edge.sourceHandle) ?? handles?.[0];
      if (!node || !handle) return [];

      return [
        {
          id: edge.id,
          x: node.internals.positionAbsolute.x + handle.x + handle.width / 2,
          y: node.internals.positionAbsolute.y + handle.y + handle.height / 2,
          position: handle.position
        }
      ];
    });

    const lines: DraggedLine[] = anchors.length
      ? anchors
      : [{ id: 'connection', x: fromX, y: fromY, position: fromPosition }];

    return (
      <g>
        {lines.map((line) => {
          const [path] = getSmoothStepPath({
            sourceX: line.x,
            sourceY: line.y,
            sourcePosition: line.position,
            targetX: toX,
            targetY: toY,
            targetPosition: toPosition
          });

          return <path key={line.id} d={path} fill='none' className='react-flow__connection-path' />;
        })}
      </g>
    );
  };

  return ReconnectConnectionLine;
};

export default createReconnectConnectionLine;
